import { api } from './api.ts';

/**
 * Starting a CABS Identity ceremony, and remembering which one is in flight.
 *
 * ## Why the browser has to remember anything at all
 *
 * One redirect URI is registered with CABS Identity for this whole deployment, so a first-use and
 * an ordinary sign-in come back to the same page. That page has to post to one of two server
 * endpoints, and only one of them can create an account.
 *
 * It must not learn which from the URL it was returned to. A callback carries whatever the caller
 * put in it, so selecting `/first-use/callback` from a query parameter would let anyone who can
 * send a person a link choose the ceremony that provisions accounts — the endpoint is the security
 * decision, and it would be made by the attacker. The ceremony is therefore recorded here, at the
 * moment this browser starts one, and read back from its own `sessionStorage`.
 *
 * ## What happens when nothing was recorded
 *
 * The ordinary sign-in runs. Not because the browser guesses, but because sign-in is the ceremony
 * that can do least: it can only reach an account this subject is already bound to, and refuses
 * `SUBJECT_NOT_BOUND` otherwise. Creating an account requires a recorded intent that this browser
 * put there, so a lost or absent record costs a person one refusal rather than silently provisioning
 * anything.
 *
 * `sessionStorage` rather than `localStorage`: the record belongs to this tab and this ceremony, and
 * should not outlive the browsing session that started it.
 */
const CEREMONY_KEY = 'gatecheck.cabs.ceremony';

export type CabsCeremony =
  | { purpose: 'SIGN_IN' }
  /** The display name a person typed for the account about to be created. */
  | { purpose: 'FIRST_USE'; displayName: string };

/** Every access guarded: a private window or blocked site data must not break sign-in. */
function remember(ceremony: CabsCeremony): void {
  try {
    window.sessionStorage.setItem(CEREMONY_KEY, JSON.stringify(ceremony));
  } catch {
    // A ceremony that cannot be recorded still completes — as a sign-in. See above.
  }
}

/**
 * Read the ceremony and forget it in the same breath.
 *
 * Single use, like the server-side transaction it corresponds to. A record left behind would apply
 * itself to whatever ceremony came next, which is how a stale first-use intent would attach to an
 * ordinary sign-in.
 */
export function takeCeremony(): CabsCeremony | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(CEREMONY_KEY);
    window.sessionStorage.removeItem(CEREMONY_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const purpose = (parsed as { purpose?: unknown }).purpose;
    if (purpose === 'FIRST_USE') {
      const displayName = (parsed as { displayName?: unknown }).displayName;
      return typeof displayName === 'string' && displayName.length > 0
        ? { purpose: 'FIRST_USE', displayName }
        : null;
    }
    return purpose === 'SIGN_IN' ? { purpose: 'SIGN_IN' } : null;
  } catch {
    return null;
  }
}

/**
 * Where to come back to once CABS has authenticated the person.
 *
 * Sent to the server, which checks it against the origins this deployment serves before the browser
 * ever leaves — and returns it from the callback rather than reading it out of the redirect. A
 * destination the caller supplies alongside the code is a destination the caller chose.
 */
function returnToFor(path: string): string {
  return new URL(path, window.location.origin).toString();
}

async function begin(endpoint: string, returnTo: string): Promise<void> {
  const started = await api.request<{ authorizationUrl: string }>(endpoint, {
    method: 'POST',
    anonymous: true,
    body: { returnTo },
  });
  // A full page navigation, not a router push: the next stop is CABS Identity.
  window.location.assign(started.authorizationUrl);
}

/**
 * Sign in with a CABS identity that is already bound to a GateCheckPro account.
 *
 * Refuses `SUBJECT_NOT_BOUND` for a stranger. That refusal is the product working: an identity
 * nobody has linked reaches no account here, and is never matched to one by email address.
 */
export async function startCabsSignIn(nextPath: string): Promise<void> {
  remember({ purpose: 'SIGN_IN' });
  await begin('/api/v1/auth/cabs/start', returnToFor(nextPath));
}

/** Create a GateCheckPro account for a CABS identity that has none, and bind the subject to it. */
export async function startCabsFirstUse(input: {
  displayName: string;
  nextPath: string;
}): Promise<void> {
  remember({ purpose: 'FIRST_USE', displayName: input.displayName });
  await begin('/api/v1/auth/cabs/first-use/start', returnToFor(input.nextPath));
}
